import { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { resolveImg, thumbImg, fromPrice, hasSizes } from "./data";
import { useMoney, useCurrency, CURRENCIES } from "./lib/currency";

/* ------------------------- icons ------------------------- */
export const Ic = ({ d, size = 20, fill = "none", label, sw = 1.6 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill={fill} stroke="currentColor" strokeWidth={sw}
    strokeLinecap="round" strokeLinejoin="round" aria-hidden={label ? undefined : true} role={label ? "img" : undefined}>
    {label ? <title>{label}</title> : null}<path d={d} />
  </svg>
);

export const P = {
  heart: "M20.8 4.6a5.5 5.5 0 0 0-7.8 0L12 5.6l-1-1a5.5 5.5 0 0 0-7.8 7.8l1 1L12 21l7.8-7.6 1-1a5.5 5.5 0 0 0 0-7.8z",
  bag: "M5 8h14l-1 12H6L5 8z M9 8V6a3 3 0 0 1 6 0v2",
  sun: "M12 8a4 4 0 1 0 0 8 4 4 0 0 0 0-8z M12 2v2 M12 20v2 M4.9 4.9l1.4 1.4 M17.7 17.7l1.4 1.4 M2 12h2 M20 12h2 M4.9 19.1l1.4-1.4 M17.7 6.3l1.4-1.4",
  moon: "M21 12.8A9 9 0 1 1 11.2 3 7 7 0 0 0 21 12.8z",
  search: "M11 4a7 7 0 1 0 0 14 7 7 0 0 0 0-14z M21 21l-4.7-4.7",
  zoom: "M11 4a7 7 0 1 0 0 14 7 7 0 0 0 0-14z M21 21l-4.7-4.7 M8 11h6 M11 8v6",
  share: "M4 12v7a1 1 0 0 0 1 1h14a1 1 0 0 0 1-1v-7 M16 6l-4-4-4 4 M12 2v13",
  x: "M18 6 6 18 M6 6l12 12",
  back: "M19 12H5 M12 19l-7-7 7-7",
  arrow: "M5 12h14 M13 6l6 6-6 6",
  left: "M15 18l-6-6 6-6",
  right: "M9 18l6-6-6-6",
  menu: "M4 8h16 M4 16h16",
  chevron: "M6 9l6 6 6-6",
  ig: "M7 2h10a5 5 0 0 1 5 5v10a5 5 0 0 1-5 5H7a5 5 0 0 1-5-5V7a5 5 0 0 1 5-5z M12 8a4 4 0 1 0 0 8 4 4 0 0 0 0-8z M17.5 6.5v.01",
  yt: "M22 8.5a3 3 0 0 0-2.1-2.2C18 5.8 12 5.8 12 5.8s-6 0-7.9.5A3 3 0 0 0 2 8.5 31 31 0 0 0 2 12a31 31 0 0 0 .1 3.5 3 3 0 0 0 2.1 2.2c1.9.5 7.8.5 7.8.5s6 0 7.9-.5a3 3 0 0 0 2.1-2.2A31 31 0 0 0 22 12a31 31 0 0 0 0-3.5z M10 9.2v5.6l5-2.8z",
  check: "M20 6 9 17l-5-5",
  plus: "M12 5v14 M5 12h14",
  minus: "M5 12h14",
  trash: "M3 6h18 M8 6V4a1 1 0 0 1 1-1h6a1 1 0 0 1 1 1v2 M19 6l-1 14a1 1 0 0 1-1 1H7a1 1 0 0 1-1-1L5 6",
  mail: "M4 4h16a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2z M22 6l-10 7L2 6",
};

/* ------------------------- scroll reveal ------------------------- */
/* Fades a block in once as it scrolls into view. Without IntersectionObserver
   (or with reduced motion) everything simply shows. */
export function Reveal({ as: Tag = "div", className = "", delay = 0, children, ...rest }) {
  const ref = useRef(null);
  const [seen, setSeen] = useState(
    typeof window === "undefined" || !("IntersectionObserver" in window)
  );
  useEffect(() => {
    if (seen || !ref.current) return undefined;
    const io = new IntersectionObserver((entries) => {
      if (entries.some((e) => e.isIntersecting)) { setSeen(true); io.disconnect(); }
    }, { rootMargin: "0px 0px -8% 0px" });
    io.observe(ref.current);
    return () => io.disconnect();
  }, [seen]);
  return (
    <Tag ref={ref} className={`kg-reveal${seen ? " in" : ""} ${className}`}
      style={delay ? { transitionDelay: `${delay}ms` } : undefined} {...rest}>{children}</Tag>
  );
}

/* ------------------------- shared pieces ------------------------- */
/* The painting on a soft "mat", like a print on a gallery wall. */
export function Matted({ art, big = false, eager = false }) {
  const ref = big ? resolveImg(art.images[0]) : thumbImg(art.images[0]);
  return (
    <div className="kg-mat">
      <img src={ref} alt={`${art.title} — ${art.medium}, ${art.dims}`} loading={eager ? "eager" : "lazy"} decoding="async" />
    </div>
  );
}

export function ArtCard({ art, onOpen, wished, onWish, eager }) {
  const money = useMoney();
  const sold = art.status === "sold";
  return (
    <article className="kg-card">
      <button className={"kg-heart" + (wished ? " on" : "")}
        aria-label={wished ? `Remove ${art.title} from wishlist` : `Add ${art.title} to wishlist`}
        aria-pressed={wished}
        onClick={() => onWish(art.id)}>
        <Ic d={P.heart} size={17} fill={wished ? "currentColor" : "none"} />
      </button>
      <a className="kg-card-link" href={`/painting/${encodeURIComponent(art.id)}`}
        aria-label={`View ${art.title}`}
        onClick={(e) => { if (e.metaKey || e.ctrlKey || e.shiftKey || e.button === 1) return; e.preventDefault(); onOpen(art.id); }}>
        <Matted art={art} eager={eager} />
        <div className="kg-card-meta">
          <div>
            <h3 className="kg-card-title">{art.title}</h3>
            <p className="kg-card-sub">{art.medium} · {art.dims}</p>
          </div>
          <div className={"kg-card-price" + (sold ? " sold" : "")}>
            {sold ? "Sold" : money(art.price)}
          </div>
        </div>
      </a>
    </article>
  );
}

/* A print / sticker / bookmark on the Prints page.

   Deliberately the same shape as ArtCard so the two pages feel like one shop,
   with two differences: it shows "from $35" because a listing has several sizes,
   and it has no wishlist heart — the wishlist is for originals someone is
   hoping to own, not for things they can buy any time. */
export function ShopCard({ item, onOpen, eager }) {
  const money = useMoney();
  const from = fromPrice(item);
  const variants = (item.variants || []).filter((v) => Number(v.price) > 0);
  /* "from $35" and "2 sizes" only make sense where there is a choice to make. */
  const sizes = hasSizes(item.kind) ? variants.length : 1;
  /* The line under the title. A print with one size should say what that size
     IS — "A4 — 21 × 30 cm" — rather than leave a blank where the useful fact
     goes. Several sizes are summarised, since the names are too long to list on
     a card and the item's own page shows them all. Stickers and bookmarks have
     no size to report, so they keep the blank line that holds the row's height. */
  const sub = !hasSizes(item.kind) ? "\u00a0"
    : sizes > 1 ? `${sizes} sizes`
    : (variants[0] && variants[0].label) || "\u00a0";
  return (
    <article className="kg-card">
      <a className="kg-card-link" href={`/prints/${encodeURIComponent(item.id)}`}
        aria-label={`View ${item.title}`}
        onClick={(e) => { if (e.metaKey || e.ctrlKey || e.shiftKey || e.button === 1) return; e.preventDefault(); onOpen(item.id); }}>
        <div className="kg-mat">
          <img src={thumbImg((item.images || [])[0])} alt={item.title}
            loading={eager ? "eager" : "lazy"} decoding="async" />
        </div>
        <div className="kg-card-meta">
          <div>
            <h3 className="kg-card-title">{item.title}</h3>
            <p className="kg-card-sub">{sub}</p>
          </div>
          <div className="kg-card-price">
            {from > 0 ? (sizes > 1 ? <>from {money(from)}</> : money(from)) : "—"}
          </div>
        </div>
      </a>
    </article>
  );
}

/* The currency switcher in the header.

   A plain <select>, on purpose: it is the one control on the site that every
   browser, screen reader and phone keyboard already knows how to operate, and
   it needs no code of its own to open, close or trap focus. The place name sits
   beside the code — "Canada · CAD" — because that is how someone recognises
   their own money at a glance.

   Currencies we have no rate for are disabled rather than hidden, so the list
   doesn't change shape under someone mid-click. */
export function CurrencyPicker() {
  const { chosen, setCode, available, ready } = useCurrency();
  return (
    <label className="kg-fx" title="Show prices in">
      <span className="kg-sr">Show prices in</span>
      <select className="kg-fx-select" value={chosen} onChange={(e) => setCode(e.target.value)}>
        {CURRENCIES.map((c) => {
          const ok = available(c.code);
          return (
            /* A greyed-out option that doesn't say why is just a dead end, so
               the reason goes in the label where it can actually be read. */
            <option key={c.code} value={c.code} disabled={!ok}>
              {c.place} · {c.code}{ok ? "" : " — rate unavailable"}
            </option>
          );
        })}
      </select>
      <Ic d={P.chevron} size={15} />
      {/* Until the rates land only Canadian dollars can be chosen; saying so
          beats a list of options that silently do nothing. */}
      {!ready && <span className="kg-sr">Other currencies are still loading</span>}
    </label>
  );
}

export function Toast({ msg }) { return msg ? <div className="kg-toast" role="status">{msg}</div> : null; }

export function SectionHead({ eyebrow, title, action, onAction }) {
  return (
    <div className="kg-sechead">
      <div>
        {eyebrow && <div className="kg-caps">{eyebrow}</div>}
        <h2 className="kg-display kg-h2">{title}</h2>
      </div>
      {action && (
        <button className="kg-textlink" onClick={onAction}>{action} <Ic d={P.arrow} size={16} /></button>
      )}
    </div>
  );
}

export function Lightbox({ src, alt, onClose }) {
  const [t, setT] = useState({ s: 1, x: 0, y: 0 });
  const drag = useRef(null);
  const pinch = useRef(null);
  const moved = useRef(false);
  const clamp = (s) => Math.min(5, Math.max(1, s));
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { window.removeEventListener("keydown", onKey); document.body.style.overflow = prev; };
  }, [onClose]);
  const zoomTo = (s) => setT((c) => { const n = clamp(s); return n === 1 ? { s: 1, x: 0, y: 0 } : { ...c, s: n }; });
  const onWheel = (e) => zoomTo(t.s * (e.deltaY < 0 ? 1.2 : 1 / 1.2));
  const dist = (a, b) => Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY);
  const onTouchStart = (e) => {
    moved.current = false;
    if (e.touches.length === 2) pinch.current = { d: dist(e.touches[0], e.touches[1]), s: t.s };
    else if (e.touches.length === 1) drag.current = { x: e.touches[0].clientX - t.x, y: e.touches[0].clientY - t.y };
  };
  const onTouchMove = (e) => {
    if (e.touches.length === 2 && pinch.current) {
      moved.current = true;
      zoomTo(pinch.current.s * (dist(e.touches[0], e.touches[1]) / pinch.current.d));
    } else if (e.touches.length === 1 && drag.current && t.s > 1) {
      moved.current = true;
      setT({ s: t.s, x: e.touches[0].clientX - drag.current.x, y: e.touches[0].clientY - drag.current.y });
    }
  };
  const onTouchEnd = () => { pinch.current = null; drag.current = null; };
  const onMouseDown = (e) => { moved.current = false; if (t.s > 1) drag.current = { x: e.clientX - t.x, y: e.clientY - t.y }; };
  const onMouseMove = (e) => {
    if (drag.current && e.buttons === 1) { moved.current = true; setT({ s: t.s, x: e.clientX - drag.current.x, y: e.clientY - drag.current.y }); }
  };
  const onMouseUp = () => { drag.current = null; };
  const onImgClick = (e) => {
    e.stopPropagation();
    if (moved.current) return;
    zoomTo(t.s > 1 ? 1 : 2.5);
  };
  return createPortal(
    <div className={"kg-lightbox" + (t.s > 1 ? " zoomed" : "")} role="dialog" aria-modal="true" aria-label={alt}
      onClick={() => { if (!moved.current) onClose(); }}
      onWheel={onWheel} onTouchStart={onTouchStart} onTouchMove={onTouchMove} onTouchEnd={onTouchEnd}
      onMouseDown={onMouseDown} onMouseMove={onMouseMove} onMouseUp={onMouseUp} onMouseLeave={onMouseUp}>
      <button className="close" onClick={(e) => { e.stopPropagation(); onClose(); }} aria-label="Close full-screen view"><Ic d={P.x} size={20} /></button>
      <img src={src} alt={alt} draggable={false} onClick={onImgClick}
        style={{ transform: `translate(${t.x}px, ${t.y}px) scale(${t.s})` }} />
      <div className="kg-lb-hint">Tap or scroll to zoom · drag to move</div>
    </div>,
    document.body
  );
}
